import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Hash, Building2, Map as MapIcon, X, ChevronRight, Loader2, Search, Layers } from "lucide-react";

const TYPE_META = {
  city: { icon: MapPin, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  postal: { icon: Hash, color: "bg-sky-100 text-sky-800 border-sky-200" },
  department: { icon: Building2, color: "bg-violet-100 text-violet-800 border-violet-200" },
  region: { icon: MapIcon, color: "bg-amber-100 text-amber-800 border-amber-200" },
};

function typeLabel(type, country, t) {
  const fr = country === "FR";
  const map = {
    city: t("radar.geo.city", "Ville"),
    postal: t("radar.geo.postal", "Code postal"),
    department: fr ? t("radar.geo.department", "Département") : t("radar.geo.province", "Province / Comté"),
    region: fr ? t("radar.geo.region", "Région") : t("radar.geo.stateRegion", "Région / État"),
  };
  return map[type] || type;
}

const chipKey = (c) => `${c.type}:${(c.postal || c.city || c.department || c.region || c.label || "").toLowerCase()}`;

// Drapeau emoji à partir du code pays ISO-2 (indicateurs régionaux).
function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  const A = 0x1f1e6;
  const up = cc.toUpperCase();
  return String.fromCodePoint(A + up.charCodeAt(0) - 65) + String.fromCodePoint(A + up.charCodeAt(1) - 65);
}

// Barre de recherche géographique + drill-down (pays → région → département → ville → code postal).
export default function GeoSelector({ country = "FR", currency = "", chips = [], setChips, t }) {
  const flag = flagEmoji(country);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [browse, setBrowse] = useState(false);
  const [browseItems, setBrowseItems] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [stack, setStack] = useState([]); // [{level,code,label}]
  const [scopedNote, setScopedNote] = useState("");
  const boxRef = useRef(null);
  const debRef = useRef(null);

  const addChip = useCallback((item) => {
    setChips((prev) => {
      if (prev.some((c) => chipKey(c) === chipKey(item))) return prev;
      return [...prev, item];
    });
  }, [setChips]);

  const removeChip = (i) => setChips((prev) => prev.filter((_, idx) => idx !== i));

  // Autocomplétion (debounce 250 ms)
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true); setOpen(true);
      try {
        const r = await api.get("/radar/geo/suggest", { params: { q: q.trim(), country, limit: 12 } });
        setResults(r.data.results || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, country]);

  // Fermer au clic extérieur
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (item) => {
    addChip(item);
    setQ("");
    setResults([]);
    setOpen(false);
  };

  // Drill-down
  const loadChildren = async (level, code, label) => {
    setBrowseLoading(true); setScopedNote("");
    try {
      const r = await api.get("/radar/geo/children", { params: { country, level, code: code || "", name: label || "" } });
      setBrowseItems(r.data.items || []);
      if (r.data.scoped_search) {
        setScopedNote(t("radar.geo.scopedNote", "Recherchez une ville ou un code postal via la barre ci-dessus."));
      }
    } catch (e) { setBrowseItems([]); }
    finally { setBrowseLoading(false); }
  };

  const openBrowse = async () => {
    const nx = !browse;
    setBrowse(nx);
    if (nx) { setStack([]); await loadChildren("country", "", ""); }
  };

  // Descendre d'un niveau (region→department→city→postal)
  const NEXT = { country: "region", region: "department", department: "city", city: "postal" };
  const drill = async (item) => {
    const next = NEXT[item.type === "region" ? "region" : item.type === "department" ? "department" : item.type === "city" ? "city" : "region"];
    // niveau de l'item cliqué détermine ce qu'on charge ensuite
    const levelMap = { region: "region", department: "department", city: "city" };
    const lvl = levelMap[item.type];
    if (!lvl || item.type === "postal") return;
    setStack((s) => [...s, { level: item.type, code: item.code, label: item.label }]);
    await loadChildren(lvl, item.code, item.label);
  };

  const goBreadcrumb = async (idx) => {
    if (idx < 0) { setStack([]); await loadChildren("country", "", ""); return; }
    const target = stack[idx];
    setStack((s) => s.slice(0, idx + 1));
    const lvl = { region: "region", department: "department", city: "city" }[target.level];
    await loadChildren(lvl, target.code, target.label);
  };

  const hasChildren = (type) => type === "region" || type === "department" || type === "city";

  return (
    <div data-testid="geo-selector">
      <Label className="text-slate-700">{t("radar.geo.title", "Localisation (ville, code postal, département, région)")}</Label>

      {/* Chips sélectionnés */}
      {chips.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="geo-chips">
          {chips.map((c, i) => {
            const M = TYPE_META[c.type] || TYPE_META.city; const Ic = M.icon;
            return (
              <span key={chipKey(c) + i} data-testid={`geo-chip-${i}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${M.color}`}>
                <Ic className="h-3 w-3" />
                {(() => { const fl = flagEmoji(c.country || country); return fl ? `${fl} ` : ""; })()}{c.label}
                <button type="button" data-testid={`geo-chip-remove-${i}`} onClick={() => removeChip(i)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"><X className="h-3 w-3" /></button>
              </span>
            );
          })}
        </div>
      )}

      {/* Barre de recherche */}
      <div ref={boxRef} className="relative mt-1.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="geo-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q.trim().length >= 2 && setOpen(true)}
            placeholder={t("radar.geo.placeholder", "Rechercher un lieu…")}
            className="h-11 pl-9"
            autoComplete="off"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[#C6A87C]" />}
        </div>

        {/* Liste des suggestions */}
        {open && (
          <div data-testid="geo-suggest-list"
            className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
            {!loading && results.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500" data-testid="geo-suggest-empty">
                {t("radar.geo.noResult", "Aucune proposition. Essayez d'autres lettres.")}
              </div>
            )}
            {results.map((r, i) => {
              const M = TYPE_META[r.type] || TYPE_META.city; const Ic = M.icon;
              return (
                <button key={r.id + i} type="button" data-testid={`geo-suggest-${r.type}-${i}`}
                  onClick={() => pick(r)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-[#C6A87C]/10">
                  <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-md border ${M.color}`}>
                    <Ic className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-[#0F172A]">{flag ? `${flag} ` : ""}{r.label}</span>
                    {r.detail && <span className="block truncate text-xs text-slate-500">{r.detail}</span>}
                  </span>
                  <span className="flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {typeLabel(r.type, country, t)}{currency ? ` · ${currency}` : ""}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Drill-down */}
      <button type="button" data-testid="geo-browse-toggle" onClick={openBrowse}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C6A87C] hover:underline">
        <Layers className="h-3.5 w-3.5" />
        {browse ? t("radar.geo.browseHide", "Masquer le parcours par zone") : t("radar.geo.browseShow", "Parcourir par zone (pays → région → …)")}
      </button>

      {browse && (
        <div data-testid="geo-browse-panel" className="mt-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          {/* Fil d'ariane */}
          <div className="mb-2 flex flex-wrap items-center gap-1 text-xs text-slate-600">
            <button type="button" data-testid="geo-crumb-root" onClick={() => goBreadcrumb(-1)}
              className="rounded px-1.5 py-0.5 font-medium hover:bg-slate-200">{country}</button>
            {stack.map((s, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <button type="button" data-testid={`geo-crumb-${i}`} onClick={() => goBreadcrumb(i)}
                  className="rounded px-1.5 py-0.5 font-medium hover:bg-slate-200">{s.label}</button>
              </span>
            ))}
          </div>
          {browseLoading ? (
            <div className="flex items-center gap-2 px-1 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("radar.geo.loading", "Chargement…")}</div>
          ) : (
            <>
              {scopedNote && <p className="mb-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">{scopedNote}</p>}
              <div className="grid max-h-64 grid-cols-2 gap-1.5 overflow-auto sm:grid-cols-3">
                {browseItems.map((it, i) => (
                  <div key={it.id + i} data-testid={`geo-browse-item-${i}`}
                    className="flex items-center justify-between gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                    <button type="button" data-testid={`geo-browse-drill-${i}`}
                      onClick={() => (hasChildren(it.type) ? drill(it) : addChip(it))}
                      className="min-w-0 flex-1 truncate text-left text-xs font-medium text-[#0F172A] hover:text-[#C6A87C]"
                      title={it.label}>{it.label}</button>
                    <button type="button" data-testid={`geo-browse-add-${i}`} onClick={() => addChip(it)}
                      title={t("radar.geo.add", "Sélectionner")}
                      className="flex-none rounded-full bg-[#C6A87C]/15 px-1.5 text-sm font-bold leading-5 text-[#C6A87C] hover:bg-[#C6A87C]/30">+</button>
                  </div>
                ))}
                {browseItems.length === 0 && !scopedNote && (
                  <p className="col-span-full px-1 py-2 text-xs text-slate-500">{t("radar.geo.browseEmpty", "Aucun élément.")}</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
